import React from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import LocalOfferIcon from '@material-ui/icons/LocalOffer'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline'
import SmsIcon from '@material-ui/icons/Sms'
import SettingsIcon from '@material-ui/icons/Settings'

import { Campaign } from '../../../models/Outreach/types'
import {
    getPrimaryCampaignDisplayStatus,
    PRIMARY_CAMPAIGN_STATUS_LABELS,
} from '../utils/campaignDisplayStatus'
import {
    useSeasonCampaigns,
    useSeasonIdFromQuery,
} from './hooks/useSeasonCampaigns'
import {
    leasingSeasonOutreachPath,
    seasonCampaignCreatePath,
    seasonCampaignDetailPath,
} from './utils/seasonCampaignUtils'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        padding: theme.spacing(3),
        maxWidth: 1200,
        margin: '0 auto',
    },
    header: {
        marginBottom: theme.spacing(3),
    },
    backButton: {
        marginBottom: theme.spacing(2),
        textTransform: 'none',
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    card: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    cardContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    iconWrap: {
        width: 40,
        height: 40,
        borderRadius: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(1.5),
    },
    emailIcon: {
        backgroundColor: '#ede9fe',
        color: '#7c3aed',
    },
    smsIcon: {
        backgroundColor: '#dbeafe',
        color: '#2563eb',
    },
    statusChip: {
        marginBottom: theme.spacing(2),
    },
    stats: {
        display: 'flex',
        gap: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
    statValue: {
        fontWeight: 700,
        fontSize: '1.25rem',
    },
    statLabel: {
        fontSize: '0.7rem',
        color: theme.palette.text.secondary,
    },
    actions: {
        marginTop: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    rowActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0,
    },
    enrolledLink: {
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
    statusActive: {
        backgroundColor: '#ecfdf5',
        color: '#047857',
    },
    statusReady: {
        backgroundColor: '#eff6ff',
        color: '#1d4ed8',
    },
    statusSetup: {
        backgroundColor: '#fef3c7',
        color: '#b45309',
    },
    customSection: {
        marginTop: theme.spacing(4),
    },
    customRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
}))

interface CampaignListCardProps {
    title: string
    channel: 'email' | 'sms'
    campaign?: Campaign
    seasonId?: number
}

const CampaignListCard: React.FC<CampaignListCardProps> = ({
    title,
    channel,
    campaign,
    seasonId,
}) => {
    const classes = useStyles()
    const isEmail = channel === 'email'

    const displayStatus = campaign
        ? getPrimaryCampaignDisplayStatus(campaign)
        : 'needs_setup'
    const statusLabel = PRIMARY_CAMPAIGN_STATUS_LABELS[displayStatus]
    const statusClass =
        displayStatus === 'active'
            ? classes.statusActive
            : displayStatus === 'ready_paused'
            ? classes.statusReady
            : classes.statusSetup

    const configureHref = campaign
        ? seasonCampaignDetailPath(campaign.id, seasonId)
        : seasonCampaignCreatePath(seasonId)
    const trackingHref = campaign
        ? seasonCampaignDetailPath(campaign.id, seasonId, 'tracking')
        : undefined

    const enrolledStat = (
        <>
            <Typography className={classes.statValue}>
                {campaign?.total_prospects ?? 0}
            </Typography>
            <Typography className={classes.statLabel}>Enrolled</Typography>
        </>
    )

    return (
        <Card className={classes.card} variant="outlined">
            <CardContent className={classes.cardContent}>
                <Box
                    className={`${classes.iconWrap} ${
                        isEmail ? classes.emailIcon : classes.smsIcon
                    }`}
                >
                    {isEmail ? <MailOutlineIcon /> : <SmsIcon />}
                </Box>
                <Typography variant="h6">{title}</Typography>
                <Chip
                    label={statusLabel}
                    size="small"
                    className={`${classes.statusChip} ${statusClass}`}
                />
                <Box className={classes.stats}>
                    <Box>
                        {trackingHref ? (
                            <Link
                                to={trackingHref}
                                className={classes.enrolledLink}
                            >
                                {enrolledStat}
                            </Link>
                        ) : (
                            enrolledStat
                        )}
                    </Box>
                    <Box>
                        <Typography className={classes.statValue}>
                            {campaign?.pending_prospects ?? 0}
                        </Typography>
                        <Typography className={classes.statLabel}>
                            Pending send
                        </Typography>
                    </Box>
                </Box>
                <Box className={classes.actions}>
                    <Button
                        component={Link}
                        to={configureHref}
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SettingsIcon />}
                    >
                        {campaign ? 'Configure' : 'Create'}
                    </Button>
                    {trackingHref && (
                        <Button
                            component={Link}
                            to={trackingHref}
                            variant="outlined"
                            color="primary"
                            fullWidth
                            startIcon={<PeopleOutlineIcon />}
                        >
                            View tracking
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}

export const SeasonCampaignsHome: React.FC = () => {
    const classes = useStyles()
    const location = useLocation()
    const history = useHistory()
    const seasonId = useSeasonIdFromQuery(location.search)

    const {
        season,
        primaryEmail,
        primaryText,
        customCampaigns,
        isLoading,
    } = useSeasonCampaigns(seasonId)

    if (seasonId == null) {
        return (
            <Box className={classes.container}>
                <Typography color="textSecondary">
                    Open campaigns from a leasing season to see season-scoped
                    outreach.
                </Typography>
                <Button
                    component={Link}
                    to={leasingSeasonOutreachPath()}
                    startIcon={<ArrowBackIcon />}
                    style={{ marginTop: 16 }}
                >
                    Go to Leasing Season
                </Button>
            </Box>
        )
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.header}>
                <Button
                    component={Link}
                    to={leasingSeasonOutreachPath(seasonId)}
                    startIcon={<ArrowBackIcon />}
                    className={classes.backButton}
                    size="small"
                >
                    Back to Leasing Season
                </Button>
                <Typography variant="h5">Season campaigns</Typography>
                <Typography variant="body2" className={classes.subtitle}>
                    {season?.name
                        ? `Configure outreach for ${season.name}. Primary campaigns auto-enroll enriched prospects.`
                        : 'Configure primary and custom campaigns for this season.'}
                </Typography>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <CampaignListCard
                        title="Primary Email Outreach"
                        channel="email"
                        campaign={primaryEmail}
                        seasonId={seasonId}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CampaignListCard
                        title="Primary Text Outreach"
                        channel="sms"
                        campaign={primaryText}
                        seasonId={seasonId}
                    />
                </Grid>
            </Grid>

            <Box className={classes.customSection}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Typography variant="h6">Custom campaigns</Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() =>
                            history.push(seasonCampaignCreatePath(seasonId))
                        }
                    >
                        New custom campaign
                    </Button>
                </Box>
                <Card variant="outlined">
                    {customCampaigns.length === 0 ? (
                        <Box p={3}>
                            <Typography color="textSecondary">
                                No custom campaigns for this season yet. Custom
                                campaigns with auto-enroll rules come in a later
                                phase; you can still create manual campaigns
                                here.
                            </Typography>
                        </Box>
                    ) : (
                        customCampaigns.map((campaign) => {
                            const displayStatus = getPrimaryCampaignDisplayStatus(
                                campaign,
                            )
                            const configureHref = seasonCampaignDetailPath(
                                campaign.id,
                                seasonId,
                            )
                            const trackingHref = seasonCampaignDetailPath(
                                campaign.id,
                                seasonId,
                                'tracking',
                            )
                            return (
                                <Box
                                    key={campaign.id}
                                    className={classes.customRow}
                                >
                                    <Box>
                                        <Typography variant="subtitle1">
                                            {campaign.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            <Link
                                                to={trackingHref}
                                                className={classes.enrolledLink}
                                            >
                                                {campaign.total_prospects ?? 0}{' '}
                                                enrolled
                                            </Link>
                                            {' · '}
                                            {
                                                PRIMARY_CAMPAIGN_STATUS_LABELS[
                                                    displayStatus
                                                ]
                                            }
                                        </Typography>
                                        {campaign.concession_names?.length > 0
                                            ? campaign.concession_names.map(
                                                  (name: string, i: number) => (
                                                      <Chip
                                                          key={i}
                                                          icon={
                                                              <LocalOfferIcon
                                                                  style={{
                                                                      fontSize: 14,
                                                                      color:
                                                                          '#047857',
                                                                  }}
                                                              />
                                                          }
                                                          label={name}
                                                          size="small"
                                                          style={{
                                                              backgroundColor:
                                                                  '#d1fae5',
                                                              color: '#047857',
                                                              fontWeight: 600,
                                                              fontSize:
                                                                  '0.625rem',
                                                              height: 22,
                                                              marginLeft: 8,
                                                              borderRadius: 6,
                                                          }}
                                                      />
                                                  ),
                                              )
                                            : null}
                                    </Box>
                                    <Box className={classes.rowActions}>
                                        <Button
                                            component={Link}
                                            to={configureHref}
                                            size="small"
                                            color="primary"
                                        >
                                            Configure
                                        </Button>
                                        <Button
                                            component={Link}
                                            to={trackingHref}
                                            size="small"
                                            color="primary"
                                            startIcon={
                                                <PeopleOutlineIcon fontSize="small" />
                                            }
                                        >
                                            View tracking
                                        </Button>
                                    </Box>
                                </Box>
                            )
                        })
                    )}
                </Card>
            </Box>
        </Box>
    )
}
